import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { isAdmin, useSessionUser } from '@/entities/session';
import { LogoutMenuItem } from '@/features/auth/logout';
import { routes } from '@/shared/config';
import { useLanguage } from '@/shared/i18n';
import { useThemeMode } from '@/shared/lib';
import { fonts, radius } from '@/shared/theme';
import { Icon, type IconName } from '@/shared/ui';

function initials(name: string | undefined) {
  if (!name) return '?';
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  const first = parts[0]?.charAt(0) ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? '') : '';
  return (first + second).toUpperCase() || '?';
}

export function AccountMenu() {
  const { t } = useTranslation();
  const user = useSessionUser();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const { isDark, toggle } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const close = () => setAnchorEl(null);

  const go = (path: string) => () => {
    close();
    navigate(path);
  };

  const items: { label: string; icon: IconName; onClick: () => void; show: boolean }[] = [
    { label: t('nav.items.profile'), icon: 'user', onClick: go(routes.profile), show: true },
    {
      label: t('header.manageUsers'),
      icon: 'users3',
      onClick: go(routes.users),
      show: isAdmin(user),
    },
    {
      label: t('nav.items.loginHistory'),
      icon: 'history',
      onClick: go(routes.loginHistory),
      // The API only mounts the login history under the admin-guarded /users router.
      show: isAdmin(user),
    },
    { label: t('header.toggleTheme'), icon: isDark ? 'sun' : 'moon', onClick: toggle, show: true },
    {
      label: t('header.languageSwitch'),
      icon: 'globe',
      onClick: () => setLanguage(language === 'de' ? 'en' : 'de'),
      show: true,
    },
  ];

  return (
    <Box
      sx={(theme) => ({
        flex: 'none',
        pl: '14px',
        borderLeft: `1px solid ${theme.vars.palette.md.border}`,
      })}
    >
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        aria-label={t('header.account')}
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: '11px',
          p: '2px 6px 2px 2px',
          borderRadius: radius.control,
          textAlign: 'left',
          '&:hover': { background: theme.vars.palette.md.panel2 },
        })}
      >
        <Avatar name={user?.username} />
        <Box sx={{ lineHeight: 1.15, '@media (max-width:560px)': { display: 'none' } }}>
          <Box sx={{ fontFamily: fonts.head, fontWeight: 700, fontSize: 13.5 }}>
            {user?.username}
          </Box>
          <Box sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            {user ? t(`roles.${user.role}`) : ''}
          </Box>
        </Box>
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 250, mt: 1.25 } } }}
      >
        <Box sx={{ p: '10px 12px 12px' }}>
          <Typography sx={{ fontFamily: fonts.head, fontWeight: 700, fontSize: 13.5 }} noWrap>
            {user?.username}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: '2px' }} noWrap>
            {user?.email}
          </Typography>
        </Box>
        <Divider sx={{ mb: '6px' }} />
        {items
          .filter((i) => i.show)
          .map((item) => (
            <MenuItem key={item.label} onClick={item.onClick}>
              <ListItemIcon>
                <Icon name={item.icon} size={16} />
              </ListItemIcon>
              {item.label}
            </MenuItem>
          ))}
        <LogoutMenuItem onDone={close} />
      </Menu>
    </Box>
  );
}

function Avatar({ name }: { name: string | undefined }) {
  return (
    <Box
      sx={(theme) => ({
        width: 40,
        height: 40,
        flex: 'none',
        borderRadius: '50%',
        background: theme.vars.palette.md.gradAvatar,
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: fonts.head,
        fontWeight: 800,
        fontSize: 14,
        boxShadow: theme.vars.palette.md.shadowSm,
      })}
    >
      {initials(name)}
    </Box>
  );
}
